import { AsyncPipe, DatePipe, DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { CacheService } from '@core/services/cache.service';
import {
    ContentfulService,
    ContentTypes,
} from '@core/services/contentful.service';
import { DeviceService } from '@core/services/device.service';
import { EnvironmentService } from '@core/services/environment.service';
import { MetadataService } from '@core/services/metadata.service';
import { SeoService } from '@core/services/seo.service';
import { WINDOW } from '@core/services/window.service';
import { documentToPlainTextString } from '@features/contentful-dynamic/rich-text/utils/convert-to-plain-text-string';
import {
    fakeAsset,
    fixAssetUrl,
} from '@features/contentful/helpers/assetHelpers';
import { KnowledgeHighlightedTextComponent } from '@features/knowledge/knowledge-highlighted-text/knowledge-highlighted-text.component';
import { KnowledgeReadNextComponent } from '@features/knowledge/knowledge-read-next/knowledge-read-next.component';
import {
    Configurations,
    CropFocus,
    LazyPictureComponent,
    Stops,
} from '@features/lazy-image/components/lazy-picture/lazy-picture.component';
import { LeadMagnetActiveCampaignComponent } from '@features/lead-magnet/components/lead-magnet-active-campaign/lead-magnet-active-campaign.component';
import { YoutubePlayerContentfulComponent } from '@features/youtube/components/youtube-player-contentful/youtube-player-contentful.component';
import { faCalendar, faClock } from '@fortawesome/free-regular-svg-icons';
import {
    Asset,
    Entry,
    KnowledgeArticle,
    KnowledgeDestination,
    Person,
} from '@generated/graphql';
import readingTime from 'reading-time';
import { combineLatest, fromEvent, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    scan,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { Article, WithContext } from 'schema-dts';
import { KnowledgeCtaComponent } from '../knowledge-cta/knowledge-cta.component';
import { KnowledgeQuickTipsComponent } from '../knowledge-quick-tips/knowledge-quick-tips.component';
import { KnowledgeVariationCardsComponent } from '../knowledge-variation-cards/knowledge-variation-cards.component';

import { ObserversModule } from '@angular/cdk/observers';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { RichTextDocumentComponent } from '../../contentful-dynamic/rich-text/rich-text-document/rich-text-document.component';
import { KnowledgeArticleBreadcrumbsComponent } from '../knowledge-article-breadcrumbs/knowledge-article-breadcrumbs.component';
import { KnowledgeArticleTocComponent } from '../knowledge-article-toc/knowledge-article-toc.component';

import { EventTrackerService } from '@core/services/event-tracker.service';
import { ANALYTICS_EVENTS } from '@core/utils/AnalyticsEvents';
import { LazyAssetComponent } from '@features/lazy-image/components/lazy-asset/lazy-asset.component';
import { LeadMagnetExitIntentComponent } from '@features/lead-magnet/components/lead-magnet-exit-intent/lead-magnet-exit-intent.component';
import { NotFoundComponent } from '@features/not-found/components/not-found/not-found.component';
import { JsonLdComponent } from '@features/seo/json-ld/json-ld.component';
import { WithLoadingPipe } from '@shared/with-loading/with-loading.pipe';
import { LoaderComponent } from '../../../shared/loader/loader.component';
import { KnowledgeArticleAuthorComponent } from '../knowledge-article-author/knowledge-article-author.component';

@Component({
    selector: 'app-knowledge-article-detail',
    templateUrl: './knowledge-article-detail.component.html',

    styles: `
        .blogBackgroundImage {
            background-image: url('/assets/images/trips/trip-background-map-new.svg');
            background-size: 100%;
            background-repeat: no-repeat space;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LoaderComponent,
        FontAwesomeModule,
        KnowledgeArticleBreadcrumbsComponent,
        KnowledgeArticleTocComponent,
        RichTextDocumentComponent,
        ObserversModule,
        KnowledgeVariationCardsComponent,
        AsyncPipe,
        DatePipe,
        LeadMagnetExitIntentComponent,
        AsyncPipe,
        DatePipe,
        LazyPictureComponent,
        WithLoadingPipe,
        JsonLdComponent,
        NotFoundComponent,
        KnowledgeArticleAuthorComponent,
        KnowledgeReadNextComponent,
        LazyAssetComponent,
    ],
})
export class KnowledgeArticleDetailComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @Input() urlSlug: Observable<string>;

    scrollTo(targetId: string): void {
        const element = document.getElementById(targetId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }

    @ViewChild('stickyContainer') stickyContainer;
    article$: Observable<KnowledgeArticle>;
    destination$: Observable<KnowledgeDestination>;
    author$: Observable<Person>;
    featuredImageConfiguration: Observable<Configurations>;
    schemaArticle: Observable<WithContext<Article>>;
    bottomScroll: Observable<number>;
    sidebarResize = new Subject();
    loading = false;
    defaultTextStyle = 'break-words mx-auto';
    readonly defaultPersonId = '59BUCq5dXdLZD6O6ATJF8D';

    footerConfiguration = {
        image: fakeAsset(
            '/assets/images/ai_footer_image.png',
            'Footer image with a hut in the mountains'
        ),
        ratioWidth: 1,
        ratioHeight: 1,
        width: 9999,
        width2x: 9999,
    };

    balloonConfiguration = {
        image: fakeAsset(
            'assets/images/trips/trip-background-balloon-new.svg',
            'Hot air balloon vector background image'
        ),
        ratioWidth: 1,
        ratioHeight: 1,
        width: 9999,
        width2x: 9999,
    };
    compassConfiguration = {
        image: fakeAsset(
            '/assets/images/trips/trip-background-compass-new.svg',
            'Compass vector background image'
        ),
        ratioWidth: 1,
        ratioHeight: 1,
        width: 9999,
        width2x: 9999,
    };

    contentfulDynamicStylesOverrides = {
        [INLINES.ENTRY_HYPERLINK]: 'text-link-blue',
        [INLINES.HYPERLINK]: 'text-link-blue',
        [BLOCKS.HEADING_2]: `${this.defaultTextStyle} font-bold font-heading mt-8 text-darkgrey text-bold`,
        [BLOCKS.HEADING_3]: `${this.defaultTextStyle} font-semibold font-heading`,
        [BLOCKS.HEADING_4]: `${this.defaultTextStyle} font-bold font-heading`,
        [BLOCKS.HEADING_5]: `${this.defaultTextStyle} font-bold font-heading`,
        [BLOCKS.HEADING_6]: `${this.defaultTextStyle} font-bold font-heading`,
        [BLOCKS.OL_LIST]: `${this.defaultTextStyle} font-serif tracking-wide list-inside leading-relaxed !pb-6 text-darkgrey text-xl`,
        [BLOCKS.UL_LIST]: `${this.defaultTextStyle} font-serif tracking-wide list-inside leading-relaxed !pb-6 text-darkgrey text-xl`,
        [BLOCKS.PARAGRAPH]: `${this.defaultTextStyle} font-serif tracking-wide leading-relaxed !pb-6 text-darkgrey text-xl`,
        [BLOCKS.QUOTE]: `${this.defaultTextStyle} block !px-8 py-8 my-6 border-l-4 md:border-l-2 border-lightgrey bg-lightergrey/75 md:rounded-r-fa font-serif tracking-wide leading-relaxed`,
        [BLOCKS.TABLE_HEADER_CELL]:
            'border border-lightgrey text-left p-4 bg-grey-100 font-serif text-darkgrey text-lg',
        [BLOCKS.TABLE_CELL]: `${this.defaultTextStyle} border border-lightgrey p-6 pl-4 tracking-wide leading-relaxed font-serif text-darkgrey text-lg`,
        [BLOCKS.EMBEDDED_ASSET]: `${this.defaultTextStyle} rounded-lg`,
    };

    contentfulDynamicComponentOverrides = {
        [ContentTypes.highlightedText]: KnowledgeHighlightedTextComponent,
        [ContentTypes.activeCampaignLeadMagnet]:
            LeadMagnetActiveCampaignComponent,
        [ContentTypes.youtubeVideo]: YoutubePlayerContentfulComponent,
        [ContentTypes.callToAction]: KnowledgeCtaComponent,
        [ContentTypes.quickTips]: KnowledgeQuickTipsComponent,
    };

    generateMetaTagsForService = (data: KnowledgeArticle): MetaDefinition[] => {
        return [
            {
                name: 'description',
                content: data.description,
            },
            {
                property: 'og:description',
                content: data.description,
            },
            {
                property: 'og:image',
                content: `${fixAssetUrl(
                    data?.featuredImageSquare?.url
                )}?w=1200&h=800&f=${
                    data?.featuredImageCropFocus || 'center'
                }&fit=fill&fm=jpg`,
            },
            {
                property: 'og:image:secure_url',
                content: `${fixAssetUrl(
                    data?.featuredImageSquare?.url
                )}?w=1200&h=800&f=${
                    data?.featuredImageCropFocus || 'center'
                }&fit=fill&fm=jpg`,
            },
            {
                property: 'og:image:type',
                content: 'image/jpeg',
            },
            {
                property: 'og:image:width',
                content: '1200',
            },
            {
                property: 'og:image:height',
                content: '800',
            },
            {
                property: 'og:type',
                content: 'article',
            },
            {
                property: 'article:section',
                content: 'Travel',
            },
            {
                property: 'article:published_time',
                content: data.sys.firstPublishedAt,
            },
            {
                property: 'article:modified_time',
                content: data.sys.publishedAt,
            },
            {
                property: 'og:updated_time',
                content: data.sys.publishedAt,
            },
        ];
    };

    relatedArticles: Observable<KnowledgeArticle[]>;
    private destroy$ = new Subject();
    resolvedLinks: Observable<Map<string, Asset | Entry>>;
    readingTime: Observable<Number>;
    faCalendar = faCalendar;
    faClock = faClock;

    constructor(
        private contentfulService: ContentfulService,
        private seoService: SeoService,
        private deviceService: DeviceService,
        private cd: ChangeDetectorRef,
        private eventTrackerService: EventTrackerService,
        private environmentService: EnvironmentService,
        private cacheService: CacheService,
        private metadataService: MetadataService,
        private router: Router,
        @Inject(WINDOW) private window: Window,
        @Inject(DOCUMENT) private document: Document
    ) {}

    ngOnInit(): void {
        this.article$ = this.urlSlug.pipe(
            tap(() => {
                this.loading = true;
                this.cd.detectChanges();
            }),
            switchMap(slug =>
                combineLatest([
                    this.contentfulService.article(slug),
                    this.contentfulService.faqOfArticle(slug),
                ]).pipe(
                    map(([article, faqs]) => ({
                        ...article,
                        faqsCollection: faqs.faqsCollection,
                    }))
                )
            ),
            tap(() => {
                this.loading = false;
                this.cd.detectChanges();
            }),
            tap(data => {
                // Adding Meta Tags from contentful Data
                this.metadataService.setTitle(data.title);

                this.generateMetaTagsForService(data).forEach(metaObj => {
                    this.metadataService.setAttribute(metaObj);
                });

                this.metadataService.setCanonicalUrl(this.router.url);
            }),
            switchMap(article => {
                if (article.author) {
                    return of(article);
                }
                // switch to a default author
                return this.contentfulService.person(this.defaultPersonId).pipe(
                    map(author => {
                        return {
                            ...article,
                            author: author.person,
                        };
                    })
                );
            }),
            shareReplay(1)
        );

        this.destination$ = this.article$.pipe(
            map(article => article.destinationsCollection.items[0]),
            shareReplay(1)
        );

        this.readingTime = this.article$.pipe(
            map(article => documentToPlainTextString(article.body.json)),
            map(plainArticleString => readingTime(plainArticleString).minutes),
            tap(readingTime => {
                this.metadataService.setAttribute({
                    name: 'twitter:card',
                    content: 'summary',
                });
                this.metadataService.setAttribute({
                    name: 'twitter:label1',
                    content: 'Est. reading time',
                });
                this.metadataService.setAttribute({
                    name: 'twitter:data1',
                    content: `${readingTime} minutes`,
                });
            }),
            shareReplay()
        );

        this.author$ = this.article$.pipe(map(article => article.author));

        this.featuredImageConfiguration = this.article$.pipe(
            filter(destination => !!destination?.featuredImageSquare?.url),
            map(destination => ({
                [Stops.FALLBACK]: {
                    image: destination.featuredImageSquare,
                    focus: destination.featuredImageCropFocus as CropFocus,
                    ratioHeight: 1,
                    ratioWidth: 1,
                    width: 1200,
                    width2x: 1200,
                },
                [Stops.BASE]: {
                    image: destination.featuredImageSquare,
                    focus: destination.featuredImageCropFocus as CropFocus,
                    ratioHeight: 1,
                    ratioWidth: 1,
                    width: 400,
                    width2x: 600,
                    width3x: 800,
                },
                [Stops.SM]: {
                    image: destination.featuredImageSquare,
                    focus: destination.featuredImageCropFocus as CropFocus,
                    ratioHeight: 1,
                    ratioWidth: 1,
                    width: 800,
                    width2x: 1000,
                    width3x: 1200,
                },
                [Stops.MD]: {
                    image: destination.featuredImageSquare,
                    focus: destination.featuredImageCropFocus as CropFocus,
                    ratioHeight: 1,
                    ratioWidth: 2,
                    width: 800,
                    width2x: 1000,
                    width3x: 1200,
                },
                [Stops.LG]: {
                    image: destination.featuredImageSquare,
                    focus: destination.featuredImageCropFocus as CropFocus,
                    ratioHeight: 1,
                    ratioWidth: 2,
                    width: 1400,
                    width2x: 1600,
                    width3x: 2000,
                },
                [Stops.XL]: {
                    image: destination.featuredImageSquare,
                    focus: destination.featuredImageCropFocus as CropFocus,
                    ratioHeight: 1,
                    ratioWidth: 2,
                    width: 1600,
                    width2x: 2000,
                    width3x: 2400,
                },
                [Stops.XXL]: {
                    image: destination.featuredImageSquare,
                    focus: destination.featuredImageCropFocus as CropFocus,
                    ratioHeight: 1,
                    ratioWidth: 3,
                    width: 2000,
                    width2x: 3000,
                    width3x: 4000,
                },
            })),
            shareReplay(1)
        );

        this.relatedArticles = this.article$.pipe(
            map(article => {
                const relatedArticles =
                    article.relatedArticlesCollection.items || [];
                return relatedArticles.filter(
                    relatedArticle => !!relatedArticle?.urlSlug
                );
            })
        );

        this.resolvedLinks = this.article$.pipe(
            map(article => [
                ...(article.body.links.assets.block || []),
                ...(article.body.links.entries.block || []),
                ...(article.body.links.entries.inline || []),
                ...(article.body.links.entries.hyperlink || []),
            ]),
            map(
                (links: (Asset | Entry)[]) =>
                    new Map(
                        links
                            .filter(item => item?.sys?.id)
                            .map(item => [item.sys.id, item])
                    )
            )
        );

        this.schemaArticle = this.article$.pipe(
            map(article => this.seoService.generateArticle(article))
        );
    }

    ngAfterViewInit(): void {
        if (this.environmentService.isBrowser()) {
            const scrollEvent = fromEvent(this.window, 'scroll', {
                passive: true,
            });

            const sidebarScrollSize = this.sidebarResize.pipe(
                startWith(1),
                switchMap(() => this.deviceService.windowSize),
                debounceTime(100),
                map(
                    value =>
                        this.stickyContainer.nativeElement.scrollHeight -
                        value.innerHeight
                ),
                map(value => Math.max(0, value))
            );
            const scrollDiff = this.deviceService.breakPointSwitch.pipe(
                filter(windowSize => windowSize.isDesktop),
                switchMap(windowSize => scrollEvent),
                startWith(0),
                map(event => this.window.scrollY),
                pairwise(),
                map(([y1, y2]) => y2 - y1)
            );
            this.bottomScroll = combineLatest([
                scrollDiff,
                sidebarScrollSize,
            ]).pipe(
                scan((acc, [value, maxScroll]) => {
                    const result = acc + value / 2;
                    return Math.max(0, Math.min(result, maxScroll));
                }, 0)
            );

            const readingProgress = scrollEvent.pipe(
                debounceTime(100),
                filter(
                    scroll =>
                        this.document.body.scrollHeight > this.window.scrollY
                ),
                map(
                    () =>
                        Math.round(
                            (this.window.scrollY /
                                this.document.body.scrollHeight) *
                                4
                        ) / 4
                ),
                distinctUntilChanged(),
                scan((all, current) => {
                    if (!all.has(current)) {
                        this.eventTrackerService.trackEvent(
                            ANALYTICS_EVENTS.KNOWLEDGE_READING_PROGRESS,
                            {
                                progress: `${current * 100}%`,
                            }
                        );
                        all.add(current);
                    }
                    return all;
                }, new Set()),
                takeUntil(this.destroy$)
            );

            readingProgress.subscribe();
        } else {
            this.bottomScroll = of(0);
        }
    }

    sidebarContentChanged($event) {
        this.sidebarResize.next('');
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
    }
}
